import React, { useEffect, useMemo, useState } from "react"

import InfoIconGreen from "@/assets/icons/base64/InfoIconGreen"
import OpenIcon from "@/assets/icons/open.svg"
import { selectedMainObligationAtom } from "@/atoms/obligations"
import {
  DEFAULT_STE_SYMBOL,
  INITIAL_REL_PRICE,
  NULL_PRICE,
  PriceKey,
  renecPriceDataAtom,
  solanaTokenOraclePricesAtom,
} from "@/atoms/priceState"
import { networkAtom } from "@/atoms/settings"
import { SelectedReserveType } from "@/atoms/shared"
import { solendUSDSObligationsStatsAtom } from "@/atoms/solendObligations"
import { supplyToEarnAPYAtom } from "@/atoms/supplyToEarnState"
import { walletAssetsAtom } from "@/atoms/wallet"
import Button from "@/components/base/Button"
import { RelendTable } from "@/components/relendTable"
import { RelendTooltip } from "@/components/relendTooltip"
import useMobileDevice from "@/hooks/useMobileDevice"
import useTrans from "@/hooks/useTrans"
import useWalletConnect from "@/hooks/useWalletConnect"
import {
  DEPOSIT,
  LOCALE_EN,
  LOCALE_VI,
  PI_RESERVE_ITEM,
  RE_NGN,
  RE_VND,
  SKELETON_VALUE,
  USDS_APY,
  WITHDRAW,
  ZERO_DECIMALS,
} from "@/utils/constants"
import { TokenSymbol, capitalizeFirstLetter, tokenDecimalDigits } from "@/utils/helpers"
import { LOGO_SIZES, getTokenLogoDisplay } from "@/utils/logo"
import { RplToken } from "@/utils/modals/RplToken"
import { formatPercent, formatToken, formatUsd } from "@/utils/numberFormatter"
import { WHITELIST_SYMBOL_SUPPLY_RESERVES, isSymbolWhitelisted } from "@/utils/whitelists"
import { Info } from "@mui/icons-material"
import { Alert, Box, Chip, Divider, Link, Stack, TableCell, TableRow, Typography } from "@mui/material"
import { REL, ReserveType, USDS } from "@renec-foundation/relend-adapter"
import BigNumber from "bignumber.js"
import { useAtomValue } from "jotai"
import Image from "next/image"
import NextLink from "next/link"
import { useRouter } from "next/router"
import { Stat } from "../../../stat"
import {
  DEFAULT_VIEW_MORE_STE_LINK,
  VIEW_MORE_STE_LINKS,
  formatAPY,
  getLinkExplorerAddress,
  getSupplyBorrowSortingList,
} from "../../commons"
import * as styles from "../styles"

const SupplyView = ({
  reserves,
  handleOpenSupplyWithdrawPopup,
}: {
  reserves: SelectedReserveType[]
  handleOpenSupplyWithdrawPopup: (item: ReserveType, type: typeof DEPOSIT | typeof WITHDRAW) => void
}) => {
  const trans = useTrans()
  const isMobileDevice = useMobileDevice()
  const { connected, isRenecChain } = useWalletConnect()

  const [hasMounted, setHasMounted] = useState(false)

  const router = useRouter()
  const locale = router.locale === LOCALE_VI ? LOCALE_VI : LOCALE_EN

  // atoms
  const walletAssets = useAtomValue(walletAssetsAtom)
  const renecPriceData = useAtomValue(renecPriceDataAtom)
  const supplyToEarnAPY = useAtomValue(supplyToEarnAPYAtom)
  const network = useAtomValue(networkAtom)
  const selectedObligation = useAtomValue(selectedMainObligationAtom)
  const solanaTokenOraclePrices = useAtomValue(solanaTokenOraclePricesAtom)
  const solendUSDSObligationsStats = useAtomValue(solendUSDSObligationsStatsAtom)

  const supplyHeaders = useMemo(
    () => (
      <>
        <TableCell>{trans.dashboard.table.asset}</TableCell>
        <TableCell>{trans.dashboard.table.total_supply}</TableCell>
        <TableCell>{trans.dashboard.table.supply_apy}</TableCell>
        <TableCell>{trans.dashboard.table.wallet_balance}</TableCell>
        <TableCell>{trans.dashboard.table.actions}</TableCell>
      </>
    ),
    [trans],
  )

  const desiredSymbolSortingOrder = useMemo(() => getSupplyBorrowSortingList(network), [network])

  const isEmptyWallet = useMemo(() => walletAssets.every((asset) => asset.amount.isZero()), [walletAssets])
  const doesHaveCollateral = useMemo(
    () => selectedObligation && selectedObligation?.deposits.length > 0,
    [selectedObligation],
  )
  const alertBridgeName = isRenecChain ? "NemoSwap" : "Jupiter Bridge"
  const alertBridgeLink = isRenecChain ? "https://nemo.renec.org/swap" : "https://jup.ag/"
  const alertEmptyWalletDisplay =
    isEmptyWallet && !doesHaveCollateral && connected ? (
      <Alert
        severity='info'
        className='w-full mb-2'
        icon={<Info className='text-lg mr-0' />}
        sx={{
          ".MuiAlert-icon": {
            marginRight: 1,
          },
        }}
      >
        {trans.dashboard.table.your_wallet_empty(
          capitalizeFirstLetter(network),
          isRenecChain,
          alertBridgeName,
          alertBridgeLink,
        )}
      </Alert>
    ) : null

  const supplyItems = reserves
    ?.sort((a, b) => {
      const indexA = desiredSymbolSortingOrder.indexOf(a.symbol)
      const indexB = desiredSymbolSortingOrder.indexOf(b.symbol)

      // If the token is not in the desiredSymbolSortingOrder, move it to the end by assigning a high index value
      const maxIndex = desiredSymbolSortingOrder.length
      return (indexA === -1 ? maxIndex : indexA) - (indexB === -1 ? maxIndex : indexB)
    })
    .map((item, index) => {
      const isGrowthHacking = [PI_RESERVE_ITEM.address].includes(item.address)
      const symbol = item.symbol
      const isUSDS = symbol === USDS

      if (!isSymbolWhitelisted(symbol, WHITELIST_SYMBOL_SUPPLY_RESERVES)) {
        return <React.Fragment key={`${symbol}-supply-${index}`}></React.Fragment>
      }
      const walletBalance =
        walletAssets.find((a) => a.mintAddress === item.mintAddress && a.symbol === symbol)?.amount ?? BigNumber(0)
      const isFiatToken = symbol === RE_VND || symbol === RE_NGN
      const reserve = reserves?.find((r) => r.symbol === symbol)
      const steAPY = supplyToEarnAPY.find((ste) => ste.account.reserve === item.address)
      const walletBalanceText = !walletBalance.isZero()
        ? formatToken(
            walletBalance,
            tokenDecimalDigits[reserve?.symbol as TokenSymbol],
            false,
            false,
            isFiatToken ? BigNumber.ROUND_UP : undefined,
            false,
            styles.semiBoldLeftCell,
          )
        : SKELETON_VALUE

      let marketPrice = formatUsd(item.minPrice)
      if (isFiatToken) {
        marketPrice = formatUsd(item.minPrice, 6)
      }

      const totalSupply = isUSDS ? solendUSDSObligationsStats.totalSupplyAmount : item.totalSupply
      const totalSupplyUsd = totalSupply.multipliedBy(item.price)
      const totalSupplyDisplay = totalSupply.isZero()
        ? SKELETON_VALUE
        : formatToken(
            totalSupply,
            tokenDecimalDigits[reserve?.symbol as TokenSymbol],
            false,
            false,
            isFiatToken ? BigNumber.ROUND_UP : undefined,
            false,
            styles.selectTokenVolume,
          )
      const totalSupplyUSDDisplay = totalSupplyUsd.isZero() ? SKELETON_VALUE : `~${formatUsd(totalSupplyUsd)}`
      const iconName = reserve ? RplToken.getTokenDisplayName(reserve.symbol, reserve.name ?? "") : null

      const steRewardSymbol = steAPY?.account.symbol ?? DEFAULT_STE_SYMBOL
      const steRewardOraclePrice =
        solanaTokenOraclePrices && steAPY ? solanaTokenOraclePrices[steAPY.account.address]?.spotPrice : 0
      const steRewardTwapFeedPrice = renecPriceData[steRewardSymbol as PriceKey] ?? item.price.toNumber()
      const lastResortRewardPrice = steRewardSymbol === REL ? INITIAL_REL_PRICE : NULL_PRICE
      const steRewardPrice =
        steRewardOraclePrice && steRewardOraclePrice > 0
          ? steRewardOraclePrice
          : isRenecChain
            ? steRewardTwapFeedPrice
            : lastResortRewardPrice
      const steAPYOnToken = item.price.isZero()
        ? 0
        : ((steAPY?.account.apy ?? 0) * steRewardPrice) / item.price.toNumber()

      const tokenSpecificSTELinks = VIEW_MORE_STE_LINKS[symbol as TokenSymbol]
      const viewMoreSTELink = tokenSpecificSTELinks ? tokenSpecificSTELinks[locale] : DEFAULT_VIEW_MORE_STE_LINK[locale]
      const currentTime = Math.floor(Date.now() / 1000)
      const isInEventTime = steAPY && steAPY.account.startTime < currentTime && currentTime < steAPY.account.endTime
      const shouldDisplaySteAPY = steAPY && steAPY.account.apy > 0 && isInEventTime

      const baseSupplyAPY = isUSDS ? new BigNumber(USDS_APY) : reserve?.supplyAPYInterest ?? BigNumber(0)

      let supplyAPYDisplay =
        shouldDisplaySteAPY && isRenecChain ? (
          <Stack gap={0}>
            <Stack direction='row' gap={0.25}>
              <Typography className='text-green-600 font-bold text-sm'>
                {formatAPY(baseSupplyAPY.plus(steAPYOnToken))}
              </Typography>
              <RelendTooltip
                title={
                  <Box>
                    <Typography component='span'>
                      {locale === LOCALE_VI ? trans.dashboard.table.supply_apy_interest : null}{" "}
                    </Typography>
                    <Typography sx={styles.greenBold} component='span'>
                      {formatAPY(baseSupplyAPY)}{" "}
                    </Typography>
                    <Typography component='span'>
                      {locale === LOCALE_EN ? trans.dashboard.table.supply_apy_interest : null} +{" "}
                    </Typography>
                    <Typography sx={styles.orangeBold} component='span'>
                      {formatAPY(steAPYOnToken, styles.orangeBold)}{" "}
                    </Typography>
                    <Typography component='span'>{`${steAPY.account.name} ${trans.dashboard.table.rewards}.`}</Typography>
                    <Typography>
                      {trans.dashboard.table.max_reward}{" "}
                      <Typography sx={styles.orangeBold} component='span'>
                        {formatToken(
                          new BigNumber(steAPY.account.totalRewards ?? "0")
                            .shiftedBy(-Number(steAPY.account.tokenDecimals))
                            .toString(),
                          ZERO_DECIMALS,
                          false,
                          false,
                          steAPY.account.tokenDecimals === 0 ? BigNumber.ROUND_UP : undefined,
                          false,
                          styles.orangeBold,
                        )}{" "}
                      </Typography>
                      <Typography component='span'>{steAPY.account.name}</Typography>
                    </Typography>
                    <Typography>
                      {trans.dashboard.table.supply_reward_information}{" "}
                      <Link component={NextLink} href={viewMoreSTELink} target='_blank' sx={styles.orangeLink}>
                        {trans.dashboard.table.here}
                      </Link>
                    </Typography>
                  </Box>
                }
                data-testid={`supply-borrow-ste-apy-${symbol}`}
              >
                <Image src={InfoIconGreen} alt='info-icon' width={16} height={16} />
              </RelendTooltip>
            </Stack>
            <Stack direction='row' gap={0.25} alignItems='center'>
              <Typography className='text-neutral-600 text-sm'>{formatPercent(steAPYOnToken)}</Typography>
              <Image
                src={steAPY.account.logoURI}
                alt={steAPY.account.symbol || ""}
                width={16}
                height={16}
                style={{ borderRadius: "100%" }}
              />
            </Stack>
          </Stack>
        ) : (
          <Typography className='text-green-600 font-bold text-sm'>{formatAPY(baseSupplyAPY)}</Typography>
        )

      if (isGrowthHacking) {
        marketPrice = "-"
        supplyAPYDisplay = <>-</>
      }

      const explorerMintURL = getLinkExplorerAddress(network, reserve?.mintAddress ?? "")
      const tokenNameDisplay = (
        <Link
          href={explorerMintURL}
          target='_blank'
          className='flex gap-1 items-center no-underline'
          data-testid={`supply-borrow-supply-token-name-${symbol}`}
        >
          <Typography className='text-neutral-900 text-sm'>{iconName}</Typography>
          <Image className='grayscale' src={OpenIcon} alt='click-icon' width={12} height={12} />
        </Link>
      )

      if (isMobileDevice) {
        return (
          <Stack
            key={`${symbol}-supply-${index}`}
            className={`${index > 0 ? "border-t border-solid border-secondary pt-6 mt-6" : ""}`}
            gap={1}
          >
            <Stack direction='row' alignItems='center' gap={1}>
              {getTokenLogoDisplay(symbol, reserve, LOGO_SIZES.LG)}
              {tokenNameDisplay}
              <Divider
                orientation='vertical'
                flexItem
                className='text-neutral-500'
                style={{
                  marginTop: "6px",
                  height: "20px",
                  width: "2px",
                }}
              />
              <Typography className='text-neutral-500 text-xs'>{marketPrice}</Typography>
              {isUSDS && (
                <RelendTooltip title={trans.dashboard.table.isolated_desc} placement='top'>
                  <Chip
                    sx={{
                      "& .MuiChip-label": {
                        px: 1,
                        fontWeight: 500,
                        fontSize: 11,
                      },
                    }}
                    clickable={false}
                    label={trans.dashboard.table.isolated}
                    className='bg-primary text-white w-fit h-fit py-[1px]'
                  />
                </RelendTooltip>
              )}
            </Stack>
            <Box>
              <Stat
                statsWrapper={styles.statWrapper}
                titleEl={<Typography sx={styles.statTitleGray}>{trans.dashboard.table.total_supply}</Typography>}
                content={
                  <Stack alignItems='end' gap={0}>
                    <Typography className='font-semibold text-neutral-900 text-sm'>
                      {totalSupplyDisplay} <span className='text-neutral-500 text-xs font-normal'>{symbol}</span>
                    </Typography>
                    {!isGrowthHacking && (
                      <Typography className='text-neutral-500 text-xs font-normal'>{totalSupplyUSDDisplay}</Typography>
                    )}
                  </Stack>
                }
                contentStyle={styles.boldCenterCell}
              />
              <Stat
                statsWrapper={styles.statWrapper}
                titleEl={<Typography sx={styles.statTitleGray}>{trans.dashboard.table.supply_apy}</Typography>}
                content={supplyAPYDisplay}
              />
              <Stat
                statsWrapper={styles.statWrapper}
                titleEl={<Typography sx={styles.statTitleGray}>{trans.dashboard.table.wallet_balance}</Typography>}
                content={walletBalanceText}
                contentStyle={styles.semiBoldCenterCell}
              />
            </Box>
            <Button
              variant='primary'
              onClick={() => {
                handleOpenSupplyWithdrawPopup(item, DEPOSIT)
              }}
              size='md'
            >
              {trans.dashboard.table.supply}
            </Button>
          </Stack>
        )
      }

      return (
        <TableRow sx={styles.tableBodyRow} key={`${symbol}-supply-${index}`}>
          <TableCell>
            <Stack direction='row' gap={1} alignItems='start'>
              {getTokenLogoDisplay(symbol, reserve, LOGO_SIZES.MD)}
              <Stack gap={0}>
                {tokenNameDisplay}
                <Typography className='text-neutral-500 text-xs'>{marketPrice}</Typography>
              </Stack>
              {isUSDS && (
                <RelendTooltip title={trans.dashboard.table.isolated_desc} placement='top'>
                  <Chip
                    sx={{
                      "& .MuiChip-label": {
                        px: 1,
                        fontWeight: 500,
                        fontSize: 11,
                      },
                    }}
                    clickable={false}
                    label={trans.dashboard.table.isolated}
                    className='bg-primary text-white w-fit h-fit py-[1px]'
                  />
                </RelendTooltip>
              )}
            </Stack>
          </TableCell>
          <TableCell>
            <Stack gap={0}>
              <Typography className='font-semibold text-neutral-900 text-sm'>
                {totalSupplyDisplay} <span className='text-neutral-500 text-xs font-normal'>{symbol}</span>
              </Typography>
              {!isGrowthHacking && (
                <Typography className='text-neutral-500 text-xs font-normal'>{totalSupplyUSDDisplay}</Typography>
              )}
            </Stack>
          </TableCell>
          <TableCell>{supplyAPYDisplay}</TableCell>
          <TableCell>
            <Typography className='text-neutral-900 font-semibold text-sm'>{walletBalanceText}</Typography>
          </TableCell>
          <TableCell>
            <Box sx={styles.actionsWrapper}>
              <Button
                name={`${symbol}-supply`}
                variant='primary'
                onClick={() => {
                  handleOpenSupplyWithdrawPopup(item, DEPOSIT)
                }}
                size='md'
                className='min-w-full'
              >
                {trans.dashboard.table.supply}
              </Button>
            </Box>
          </TableCell>
        </TableRow>
      )
    })

  const isSupplyEmpty = !supplyItems || (supplyItems && supplyItems.length === 0)

  // This is for the tabbing. This component will be rendered on client-side
  // Will be added for skeleton layout in the future.
  useEffect(() => {
    setHasMounted(true)
  }, [])

  if (!hasMounted) {
    return <></>
  }

  return (
    <Stack data-testid='overview-supply-table-view'>
      {!isMobileDevice && (
        <RelendTable
          loading={isSupplyEmpty}
          headers={supplyHeaders}
          items={supplyItems}
          alert={alertEmptyWalletDisplay}
        />
      )}
      {isMobileDevice && (
        <>
          {alertEmptyWalletDisplay}
          {supplyItems}
        </>
      )}
    </Stack>
  )
}

export default SupplyView
